import { Island } from '../island.class';
import { Islands } from '../islands.class';

// Island joined through `island`'s own LEFT side panel, or null. Adjacency is decided by which faces connect, never by coordinates or padding.
const leftNeighborOf = (island: Island): Island | null => {
	const leftPanel = island.frontPanel?.getLeftPanel();
	if (!leftPanel) return null;
	return island.connections.getConnectionsBySourcePanelId(leftPanel.id)
		.map((connection) => connection.targetIsland)
		.find((target): target is Island => target instanceof Island) ?? null;
};

// Island joined through `island`'s own RIGHT side panel, or null.
const rightNeighborOf = (island: Island): Island | null => {
	const rightPanel = island.frontPanel?.getRightPanel();
	if (!rightPanel) return null;
	return island.connections.getConnectionsBySourcePanelId(rightPanel.id)
		.map((connection) => connection.targetIsland)
		.find((target): target is Island => target instanceof Island) ?? null;
};

// Galley = joined to another island by a FRONT-to-FRONT or BACK-to-BACK face (never a side panel), so it is never part of the left/right chain.
const isGalleyIsland = (island: Island): boolean => {
	return island.connections.children.some((connection) => {
		const { sourcePanel, targetPanel, targetIsland } = connection;
		if (!(targetIsland instanceof Island)) return false;
		const frontToFront = Boolean(sourcePanel?.isFrontPanel && targetPanel?.isFrontPanel);
		const backToBack = Boolean(sourcePanel?.isBackPanel && targetPanel?.isBackPanel);
		return frontToFront || backToBack;
	});
};

// Follow neighbour connections outward from `from` one island at a time; the visited set guarantees termination on looping or self-referential data.
const walkChainSide = (from: Island, neighborOf: (island: Island) => Island | null): Array<Island> => {
	const chain: Array<Island> = [];
	const visited = new Set<string>([from.id]);
	let cursor = from;
	let next = neighborOf(cursor);
	while (next && !visited.has(next.id)) {
		chain.push(next);
		visited.add(next.id);
		cursor = next;
		next = neighborOf(cursor);
	}
	return chain;
};

export const getOrderedChildrenCfn = (islands: Islands): Array<Island> => {
	const all = islands.children;
	if (all.length <= 1) return [...all];

	const base = islands.getDefaultIsland() ?? all[0];
	// Walked independently in each direction from `base` (not "find the left terminus, then walk
	// right from there") -- a corner-style join (a side panel meeting another island's front panel) isn't its own inverse, so resuming rightward from the leftmost island can silently drop `base`.
	const chain = [...walkChainSide(base, leftNeighborOf).reverse(), base, ...walkChainSide(base, rightNeighborOf)];
	const chainIds = new Set(chain.map((island) => island.id));

	// Galleys, then any fully disconnected island, follow the chain leftmost-first by overhead position (origin.x, origin.z tiebreaker) — coordinates are read only here.
	const byOverheadPosition = (a: Island, b: Island): number => (a.origin.x - b.origin.x) || (a.origin.z - b.origin.z);
	const remaining = all.filter((island) => !chainIds.has(island.id));
	const galleys = remaining.filter(isGalleyIsland).sort(byOverheadPosition);
	const galleyIds = new Set(galleys.map((island) => island.id));
	const disconnected = remaining.filter((island) => !galleyIds.has(island.id)).sort(byOverheadPosition);

	return [...chain, ...galleys, ...disconnected];
};
